import { useTranslation } from 'react-i18next';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { CSSProperties, ReactNode } from 'react';

/**
 * Rows reordered by dragging their handle: pointer drag after 4px, or Space /
 * Enter on the handle to pick up, arrows to move, Space / Enter to drop
 * (Escape cancels), announced to screen readers.
 */
export function SortableRows({
  ids,
  itemTitle,
  onMove,
  children
}: {
  /** Row ids in display order. */
  ids: ReadonlyArray<string>;
  /** Names a row in announcements ("Language 2"). */
  itemTitle: string;
  onMove(from: number, to: number): void;
  children: ReactNode;
}) {
  const { t } = useTranslation('patterns', { keyPrefix: 'RowList' });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );
  const position = (id: UniqueIdentifier) => ids.indexOf(String(id)) + 1;

  function onDragEnd({ active, over }: DragEndEvent) {
    if (over === null) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from !== -1 && to !== -1 && from !== to) onMove(from, to);
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      // Vertical list: scroll the host up / down near its edges, never sideways.
      autoScroll={{ threshold: { x: 0, y: 0.2 } }}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={onDragEnd}
      accessibility={{
        screenReaderInstructions: {
          draggable: t('reorderInstructions', {
            item: itemTitle.toLowerCase()
          })
        },
        announcements: {
          onDragStart: ({ active }) =>
            t('pickedUp', {
              item: itemTitle,
              position: position(active.id),
              count: ids.length
            }),
          onDragOver: ({ over }) =>
            over === null
              ? t('notOver')
              : t('moving', { position: position(over.id), count: ids.length }),
          onDragEnd: ({ over }) =>
            over === null
              ? t('droppedUnchanged')
              : t('dropped', {
                  position: position(over.id),
                  count: ids.length
                }),
          onDragCancel: ({ active }) =>
            t('cancelled', {
              item: itemTitle,
              position: position(active.id),
              count: ids.length
            })
        }
      }}
    >
      <SortableContext items={[...ids]} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}

/** One row of `SortableRows`: spread `rowProps` on the row and `handleProps` on its handle (a button). */
export function useSortableRow(id: string, enabled: boolean) {
  const {
    setNodeRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging
  } = useSortable({ id, disabled: !enabled });
  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    // The dragged row follows the pointer as is: a row's own `transition-all`
    // would make it lag behind.
    transition: isDragging ? 'none' : (transition ?? undefined),
    ...(isDragging ? { position: 'relative', zIndex: 10 } : {})
  };
  return {
    isDragging,
    rowProps: { ref: setNodeRef, style, 'data-dragging': isDragging },
    handleProps: { ref: setActivatorNodeRef, ...attributes, ...listeners }
  };
}
